/**
 * Транспорт HTTP для браузерных поверхностей — мини-аппа и админки.
 *
 * Ошибки приходят в едином формате { error: { code, message, fields } },
 * поэтому разбираются один раз здесь, а не в каждом экране и не в каждой
 * поверхности заново.
 *
 * Пакет отдаётся исходниками, без `dist`: собирать его отдельно не нужно.
 * Бот сюда не ходит — у него другой транспорт (абсолютный адрес, секрет
 * в заголовке, ошибки гасятся в null), и общий код был бы сложнее двух копий.
 */
import type { ApiErrorBody, ErrorCode } from '@arbuz/shared';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.fields = body.error.fields;
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // При FormData заголовок Content-Type ставит браузер — вместе с boundary.
  // Свой заголовок его затрёт, и сервер не разберёт multipart.
  const isFormData = init?.body instanceof FormData;

  const res = await fetch(path, {
    ...init,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
    // Сессия живёт в httpOnly-cookie
    credentials: 'same-origin',
  });

  if (res.status === 204) return undefined as T;

  const data: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    if (data && typeof data === 'object' && 'error' in data) {
      throw new ApiError(res.status, data as ApiErrorBody);
    }
    throw new ApiError(res.status, {
      error: { code: 'INTERNAL', message: 'Сервис недоступен', fields: null },
    });
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : (body instanceof FormData ? body : JSON.stringify(body)),
      ...(headers ? { headers } : {}),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  // PUT нужен там, где ресурс заменяется целиком, а не правится по частям:
  // расписание витрины отправляется всеми семью днями сразу.
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
